/**
 * Geometry of the visual viewport (the part of the page actually on screen),
 * and of the full-screen mobile sheets that must stay inside it.
 *
 * iOS Safari never shrinks the layout viewport for the on-screen keyboard:
 * `position: fixed` boxes stay laid out against the full screen, the keyboard
 * covers their bottom, and focusing a field low on screen makes Safari pan the
 * visual viewport down (`offsetTop` > 0), which pushes the top of a fixed sheet
 * off screen. A sheet that follows both `height` and `offsetTop` stays exactly
 * on the visible part of the screen, above the keyboard.
 */

export interface VisualViewportBox {
  /** Visible height in CSS pixels, without the on-screen keyboard. */
  height: number;
  /** Distance from the top of the layout viewport to the top of the visible area. */
  offsetTop: number;
  /** Pinch-zoom factor, 1 when the page is not zoomed. */
  scale: number;
}

/** Below this, a visual viewport shorter than the layout one is browser chrome, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 150;

export function readVisualViewport(): VisualViewportBox {
  if (typeof window === "undefined") {
    return { height: 0, offsetTop: 0, scale: 1 };
  }
  const vv = window.visualViewport;
  return vv
    ? { height: vv.height, offsetTop: vv.offsetTop, scale: vv.scale }
    : { height: window.innerHeight, offsetTop: 0, scale: 1 };
}

export function sameVisualViewport(a: VisualViewportBox, b: VisualViewportBox): boolean {
  return a.height === b.height && a.offsetTop === b.offsetTop && a.scale === b.scale;
}

export interface MobileSheetGeometry {
  /** Top of the sheet in layout viewport coordinates. */
  top: number;
  height: number;
  /** The keyboard covers the home indicator, so the sheet drops its bottom safe-area padding. */
  keyboardOpen: boolean;
}

/**
 * Where a full-screen mobile sheet goes so that its header, its field and its
 * footer all sit on the visible area, above the keyboard.
 *
 * While the page is pinch-zoomed the sheet stays pinned to the top of the
 * layout viewport, so that panning the zoomed page still reveals the rest of
 * the sheet instead of dragging the sheet along.
 */
export function mobileSheetGeometry(
  viewport: VisualViewportBox,
  layoutHeight: number,
): MobileSheetGeometry {
  const zoomed = Math.abs(viewport.scale - 1) > 0.01;
  const keyboardOpen = !zoomed && layoutHeight - viewport.height > KEYBOARD_MIN_HEIGHT;
  return {
    top: zoomed ? 0 : Math.max(0, viewport.offsetTop),
    height: viewport.height,
    keyboardOpen,
  };
}
